import { FilterSeparatorsEnum } from '../enums';
import { IDocument, IDocumentEntity, IGuarantor, ILeadEntity, ILeadProductEntity, ILeadValues, IProduct, IUserEntity, UploadedFile } from '../interfaces';
import { KeyValueRecord } from '../types/key-value-record';
import _ from 'lodash';
import dayjs from 'dayjs';

/**
 * @function getEnumeratorValues
 * @description Returns the values from the given enumerator.
 * @param {Record<string, T>} enumerator Enumerator.
 * @returns {T[]}
 */
export function getEnumeratorValues<T>(enumerator: Record<string, T>): T[] {
    return Object.values(enumerator) as T[];
}

/**
 * @function getEnumeratorKeys
 * @description Returns the keys from the given enumerator.
 * @param {Record<string, T>} enumerator Enumerator.
 * @returns {T[]}
 */
export function getEnumeratorKeys<T>(enumerator: Record<string, T>): string[] {
    return Object.keys(enumerator);
}

/**
 * @function nameToInitials
 * @description Returns the initials of the name, first and last part of name initials E,G John Doe => JD.
 * @param {string} fullName name.
 * @param {boolean} uppercase  {optional} transform to upper case or not.
 * @returns {string}
 */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export function nameToInitials(fullName: string, uppercase: boolean = false): string {
    const words: string[] = fullName.trim().split(' ');

    if (!words.length) return '';

    return words.reduce(word => (uppercase ? word.charAt(0).toLocaleUpperCase() : word.charAt(0)), '');
}

/**
 * @function addLeadingZeros Pads the given number with zeros.
 * @description Adds leading zeros to the given string until the result length is of the given result length.
 * @param {number} number Number.
 * @param {number} resultLength Result length.
 * @returns {string}
 */
export function addLeadingZeros(number: number, resultLength: number): string {
    let result = String(number);

    while (result.length < resultLength) result = '0' + result;

    return result;
}

/**
 * @function transformToFormattedMinutes
 * @description Returns time difference in readable format. e.g. 2 days 4 hrs and 3 minutes
 * @param {number} incomingMilliseconds number.
 * @param {boolean} returnedDetailedTime boolean. {optional} If we want detailed time returned, pass this parameter.
 * Else this function will return in hours and minutes
 * @returns {string} formatted string
 */
export function transformToFormattedMinutes(incomingMilliseconds: number, returnedDetailedTime?: boolean): string {
    let minutes: number = Math.floor(incomingMilliseconds / 1000 / 60),
        hours: number = Math.floor(Math.abs(minutes / 60));

    const days: number = Math.floor(Math.abs(hours / 24)),
        formatForOne = (value: number, string: string) => `${value} ${value === 1 ? string : string + 's'}`;

    if (hours) minutes = minutes % 60;

    if (days) hours = hours % 24;

    if (!minutes) return formatForOne(minutes, 'min');

    if (!hours) return `${addLeadingZeros(minutes, 2)} ${formatForOne(minutes, 'min')}`;

    if (!minutes) return `${addLeadingZeros(hours, 2)} ${formatForOne(hours, 'hr')}`;

    if (!days)
        return returnedDetailedTime
            ? `${addLeadingZeros(hours, 2)} ${formatForOne(hours, 'hr')} and ${addLeadingZeros(minutes, 2)} ${formatForOne(minutes, 'min')}`
            : `${addLeadingZeros(hours, 2)} ${formatForOne(hours, 'hr')}`;

    return returnedDetailedTime
        ? `${addLeadingZeros(days, 2)} ${formatForOne(days, 'day')} and ${addLeadingZeros(hours, 2)} ${formatForOne(hours, 'hr')} and ${addLeadingZeros(minutes, 2)} ${formatForOne(minutes, 'min')}`
        : `${addLeadingZeros(days, 2)} ${formatForOne(days, 'day')}`;
}

/**
    @function transformToFormattedTime
    @description Returns formatted time in DD/MM/YYYY HH:MM:AM/PM or DD/MM/YYYY
    @param {number| number} dateInMillisecondsOrISOString number<or string date.
    @param {string} delimiter {optional} delimiter to pass between the dates. e.g. if you pass '-', this function will return DD-MM-YYYY.
    @param {boolean} returnedDetailedTime {optional} returns detailed time in DD/MM/YYYY HH:MM:AM/PM else returns DD/MM/YYYY.
    @returns {string} formatted time string
*/
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export function transformToFormattedTime(dateInMillisecondsOrISOString: Date | number | string = Date.now(), delimiter: string = '/', returnedDetailedTime: boolean = true): string {
    const now = new Date(),
        date = new Date(dateInMillisecondsOrISOString),
        year = date.getFullYear().toString().slice(2);

    let hours = date.getHours();

    let ampm = 'am';

    if (hours > 11) {
        if (hours > 12) hours = hours % 12;

        ampm = 'pm';
    }

    if (returnedDetailedTime && (now.getDate() !== date.getDate() || now.getMonth() !== date.getMonth() || now.getFullYear() !== date.getFullYear()))
        return `${addLeadingZeros(date.getDate(), 2)}${delimiter}${addLeadingZeros(date.getMonth() + 1, 2)}${delimiter}${year} ${addLeadingZeros(hours, 2)}:${addLeadingZeros(
            date.getMinutes(),
            2
        )} ${ampm}`;

    if (now.getDate() !== date.getDate() || now.getMonth() !== date.getMonth() || now.getFullYear() !== date.getFullYear())
        return `${addLeadingZeros(date.getDate(), 2)}${delimiter}${addLeadingZeros(date.getMonth() + 1, 2)}${delimiter}${year}`;

    return `${addLeadingZeros(hours, 2)}:${addLeadingZeros(date.getMinutes(), 2)} ${ampm}`;
}

/**
 * Converts a given date into formatted time WRT given delimiter. Default delimiter is `/`
 * @param date Date to convert
 * @returns formatted string
 */
export function convertTimeToDDMMYYYYHHMM(date: number | Date | undefined, delimiter = '/'): string {
    return !date ? '' : transformToFormattedTime(typeof date === 'number' ? date : date.getTime(), delimiter, true);
}

/**
 * recursively remove all the tabs, next lines, more than one spaces, from properties of object(s) which are strings and also trims the edges
 * @param {KeyValueRecord} object  object for whom all the properties have to be cleaned
 * @returns cleaned object or the cleaned string
 */
export function removeWhiteSpaceFromAllProperties<T = KeyValueRecord>(object: T | string): T | T[] | string {
    const clean = (str: string) =>
        str
            .replace(/[\n\r\u2028]/g, ' ')
            .replace(/\s\s+/g, ' ')
            .trim();

    if (typeof object === 'string') return clean(object);

    if (Array.isArray(object)) return object.map(item => removeWhiteSpaceFromAllProperties(item));

    const newCleanedObject = JSON.parse(JSON.stringify(object)); // for deep copy. not bringing lodash.

    for (const key in object)
        if (!object[key]) continue;
        else newCleanedObject[key] = removeWhiteSpaceFromAllProperties(newCleanedObject[key]);

    return newCleanedObject;
}

/**
 * gives a random number within the given boundary
 * @param {number} min minimum number
 * @param {number} max max munber
 * @returns random number
 */
export function getRandomNumberBetweenBoundaries(min: number, max: number): number {
    return parseFloat((Math.random() * (max - min) + min).toFixed(0));
}

/**
 * lowercases all the properties of an object which are strings
 * @param {KeyValueRecord} object the object which has to props to do lowercase
 * @param {string[]} whitelistProperties {optional} property keys which are not to be lowercased
 */
export function recursivelyLowercaseObjectProperties(object: KeyValueRecord, whitelistProperties: string[] = []): KeyValueRecord {
    const newCleanOjbect = JSON.parse(JSON.stringify(object));

    for (const key in newCleanOjbect)
        if (typeof newCleanOjbect[key] === 'string') {
            if (!whitelistProperties.includes(key)) newCleanOjbect[key] = (newCleanOjbect[key] as string).toLocaleLowerCase();
        } else newCleanOjbect[key] = recursivelyLowercaseObjectProperties(newCleanOjbect[key]);

    return newCleanOjbect;
}

/**
 * make a function which takes a string and converts into a nested object separating on the `splitOn` parameter. e.g
 * example: "option" should be converted to { option: true }
 * example2: "option.option2" should be converted to { option: { option2: true } }
 * example3: "option.option2.option3" should be converted to { option: { option2: { option3: true } } }
 * @param {string} string string to convert
 * @param {string} splitOn split the string on this separator
 * @param {boolean | string | number} value value to be added
 * @param {KeyValueRecord} existingObject an object in which this needs to be appended
 * @returns nested object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertStringToNestedObject = (
    string: string,
    splitOn: string = FilterSeparatorsEnum.FULL_STOP,
    value: boolean | string | number = true,
    existingObject: KeyValueRecord = {}
): KeyValueRecord => {
    const [firstValue, ...others] = string.split(splitOn);

    if (!others.length)
        return {
            ...existingObject,
            [firstValue]: value,
        };

    existingObject = {
        ...existingObject,
        [firstValue]: convertStringToNestedObject(others.join(splitOn), splitOn, value, existingObject[firstValue] || {}),
    };

    return existingObject;
};

/**
 * Add wait for given seconds
 * @param {number} seconds
 * @returns a void promise
 */
export const WAIT = async (seconds: number): Promise<void> =>
    new Promise(resolve => {
        setTimeout(resolve, seconds * 1000);
    });

//phone and cnic regex
export const regexCollection = {
    phone: new RegExp('^((03)([0-9]{9}))$'),
    cnic: new RegExp('^((?!0)([0-9]{5}[-][0-9]{7}[-][0-9]))$'),
};

//removes key where value is empty
export const removeEmptyValues = (values: KeyValueRecord) => {
    const cleanedValues = { ...values };
    for (const key in cleanedValues) {
        if (!cleanedValues[key]) {
            delete cleanedValues[key];
        }
    }
    return cleanedValues;
};

//removes key where value is empty
export const getFutureDate = (monthsToAdd: number) => {
    const today = new Date();
    const currentDay = today.getDate();

    // Create a new date instance and add months
    const futureDate = new Date(today.getFullYear(), today.getMonth() + monthsToAdd, currentDay);

    // If the day changed, it means the month didn't have that date, so set to last day of that month
    if (futureDate.getDate() !== currentDay) {
        futureDate.setDate(0); // Last day of the previous month
    }

    // Format the date as dd/MM/yyyy
    const day = String(futureDate.getDate()).padStart(2, '0');
    const month = String(futureDate.getMonth() + 1).padStart(2, '0');
    const year = futureDate.getFullYear();

    return `${day}/${month}/${year}`;
};

export const getAllCombinedData = (leadInfo: ILeadValues, leadProducts: IProduct[], leadGurantor: IGuarantor[], leadDocuments: IDocument | undefined) => {
    const formData = new FormData();

    for (const [key, value] of Object.entries(leadInfo)) {
        if (key === 'comments' && value) {
            formData.append('comment', value);
        } else if (value?.toString()) formData.append(key, value);
    }

    if (leadProducts?.length) {
        for (const productKey in leadProducts) {
            const product = leadProducts[productKey];
            for (const [key, value] of Object.entries(product)) {
                const checkChargesCondition = value && value !== '0' && value !== 0;

                if (key !== 'monthlyInstallment' && key !== 'lastInstallment' && key !== 'total_price') {
                    if (key === 'delivery_charges') formData.append(`products[${productKey}][${key}]`, checkChargesCondition ? '400' : '0');
                    else if (key === 'service_charges') formData.append(`products[${productKey}][${key}]`, checkChargesCondition ? '600' : '0');
                    else if (value?.toString()) formData.append(`products[${productKey}][${key}]`, value);
                }
            }
        }
    }

    if (leadGurantor?.length) {
        // payload.guarantors = leadGurantor;
        for (const guarantorKey in leadGurantor) {
            const guarantor = leadGurantor[guarantorKey];

            for (const [key, value] of Object.entries(guarantor)) {
                if (key === 'documents') {
                    guarantor.documents.forEach((doc, index) => {
                        const name = doc.name;
                        const files = doc.files;
                        for (const file of files) {
                            if (file) formData.append(`guarantors[${guarantorKey}][${name}]`, file.file as File);
                        }
                    });
                } else {
                    if (value?.toString()) formData.append(`guarantors[${guarantorKey}][${key}]`, value);
                }
            }
        }
    }

    if (leadDocuments && Object.keys(leadDocuments)) {
        Object.keys(leadDocuments).forEach(dockey => {
            const files = leadDocuments[dockey as keyof IDocument];
            if (files?.length) {
                for (const file of files) {
                    if (file.file) formData.append(`lead_documents[${dockey}]`, file.file as File);
                }
            }
        });
    }
    return formData;
};

export const formateDbProducts = (leadProducts: ILeadProductEntity[]): IProduct[] => {
    return leadProducts.map(prod => {
        const checkServiceChargesCondition = prod.service_charges && prod.service_charges.toString() !== '0' && prod.service_charges !== 0;
        const checkDeliveryChargesCondition = prod.delivery_charges && prod.delivery_charges.toString() !== '0' && prod.delivery_charges !== 0;

        return {
            id: prod.id,
            product_type_id: prod.product_type_id,
            quantity: prod.quantity?.toString(),
            brand_id: prod.brand_id,
            product_name: prod.product_name,
            product_description: prod.product_description,
            locking_charges: prod.locking_charges?.toString(),
            delivery_charges: checkDeliveryChargesCondition ? true : false,
            service_charges: checkServiceChargesCondition ? true : false,
            down_payment: prod.down_payment?.toString(),
            token_amount: prod.token_amount?.toString(),
            apr: prod.apr?.toString(),
            retail_price: prod.retail_price?.toString(),
            instalment_plan_id: prod.instalment_plan_id,
            monthlyInstallment: '',
            lastInstallment: '',
            total_price: '',
        };
    });
};

export const formateDbDocuments = (dbdocuments: IDocumentEntity[]): IDocument => {
    const documents: IDocument = {
        profile_pic: [],
        cnic: [],
        bank_statment: [],
        bills: [],
        salary_slip: [],
        cheque: [],
        other: [],
    };

    dbdocuments?.forEach(doc => {
        if (doc.misc_details) {
            const documentType = JSON.parse(doc.misc_details as unknown as string).name as keyof IDocument;
            if (documents[documentType]) {
                documents[documentType].push({ uid: doc.id, url: doc.url, name: doc.name });
            } else {
                documents[documentType] = [{ uid: doc.id, url: doc.url, name: doc.name }];
            }
        }
    });
    return documents;
};

export const formateDbGurantors = (guarantors: IUserEntity[]): IGuarantor[] => {
    return guarantors?.map(gu => {
        const guDocuments: Record<string, UploadedFile[]> = {};

        gu.documents?.forEach(doc => {
            if (doc.misc_details) {
                const documentType = JSON.parse(doc.misc_details as unknown as string).name;
                if (guDocuments[documentType]) {
                    guDocuments[documentType].push({ uid: doc.id, url: doc.url });
                } else {
                    guDocuments[documentType] = [{ uid: doc.id, url: doc.url }];
                }
            }
        });

        return {
            id: gu.id,
            first_name: gu.first_name,
            last_name: gu.last_name || '',
            cnic_number: gu.cnic_number,
            email: gu.email,
            primary_mobile: gu.primary_mobile,
            secondary_mobile: gu.secondary_mobile || '',
            province: gu.province || '',
            city: gu.city || '',
            documents:
                Object.entries(guDocuments)?.map(([key, value]) => {
                    return {
                        name: key,
                        files: value,
                    };
                }) ?? [],
        };
    });
};

export const getUpdatedAllCombinedData = (leadEntity: ILeadEntity | null, leadInfo: ILeadValues, leadProducts: IProduct[], leadGurantor: IGuarantor[], leadDocuments: IDocument | undefined) => {
    const formData = new FormData();

    // process lead info
    const updatedLeadsInfo: Partial<ILeadValues> = {};

    (Object.keys(leadInfo) as Array<keyof ILeadValues>).forEach(field => {
        if (field === 'comments') {
            leadInfo[field] && formData.append('comment', leadInfo[field] as string);
        } else if (leadEntity?.[field] !== leadInfo[field]) {
            updatedLeadsInfo[field] = leadInfo[field] || '';
        }
    });

    for (const [key, value] of Object.entries(updatedLeadsInfo)) {
        formData.append(key, value);
    }

    // process  products
    const updateProducts: Record<string, any>[] = [];

    if (leadProducts?.length) {
        for (const product of leadProducts) {
            const existingProduct = leadEntity?.products.find(pro => pro.id === product.id);

            if (!existingProduct) {
                updateProducts.push(product);
                continue; // Skip to the next iteration
            }

            const updatedProduct: Record<string, any> = { id: existingProduct.id };

            // List of fields to check for updates
            const fieldsToCheck: (keyof typeof product)[] = [
                'product_type_id',
                'quantity',
                'brand_id',
                'product_name',
                'product_description',
                'locking_charges',
                'down_payment',
                'token_amount',
                'apr',
                'retail_price',
                'instalment_plan_id',
            ];

            fieldsToCheck.forEach(field => {
                if ((existingProduct as any)[field]?.toString() !== product[field]?.toString()) {
                    updatedProduct[field] = product[field];
                }
            });

            // Handle delivery and service charges separately
            const deliveryPrice = product.delivery_charges ? '400' : '0';
            if (existingProduct.delivery_charges?.toString() !== deliveryPrice) {
                updatedProduct.delivery_charges = deliveryPrice;
            }

            const servicePrice = product.service_charges ? '600' : '0';
            if (existingProduct.service_charges?.toString() !== servicePrice) {
                updatedProduct.service_charges = servicePrice;
            }

            // Push only if there are updates
            if (updatedProduct && Object.keys(updatedProduct)?.length) {
                updateProducts.push(updatedProduct);
            }
        }
    }

    if (updateProducts?.length) {
        for (const productKey in updateProducts) {
            const product = updateProducts[productKey];
            for (const [key, value] of Object.entries(product)) {
                const checkChargesCondition = value && value !== '0' && value !== 0;
                if (key !== 'monthlyInstallment' && key !== 'lastInstallment' && key !== 'total_price') {
                    if (key === 'delivery_charges') formData.append(`products[${productKey}][${key}]`, checkChargesCondition ? '400' : '0');
                    else if (key === 'service_charges') formData.append(`products[${productKey}][${key}]`, checkChargesCondition ? '600' : '0');
                    else if (value?.toString()) formData.append(`products[${productKey}][${key}]`, value);
                }
            }
        }
    }

    // process guarantors

    const updateGurators: Record<string, any>[] = [];
    if (leadGurantor?.length) {
        for (const guarantor of leadGurantor) {
            const existingGurator = leadEntity?.guarantors.find(gu => gu.id === guarantor.id);

            if (!existingGurator) {
                updateGurators.push(guarantor);
                continue;
            }

            const updatedGurantor: Record<string, any> = { id: guarantor.id };

            // List of fields to check for updates
            const fieldsToCheck: (keyof typeof guarantor)[] = ['first_name', 'last_name', 'cnic_number', 'email', 'primary_mobile', 'secondary_mobile', 'province', 'city'];

            fieldsToCheck.forEach(field => {
                if ((existingGurator as any)[field]?.toString() !== guarantor[field]?.toString()) {
                    updatedGurantor[field] = guarantor[field];
                }
            });
            if (guarantor?.documents?.length) {
                guarantor.documents.forEach(doc => {
                    const name = doc.name;
                    const files = doc.files;
                    const filterdFiles = [];
                    for (const file of files) {
                        if (file?.file) filterdFiles.push(file);
                    }

                    if (filterdFiles?.length) {
                        if (updatedGurantor.documents) updatedGurantor.documents.push({ name, files: filterdFiles });
                        else updatedGurantor.documents = [{ name, files: filterdFiles }];
                    }
                });
            }

            // Push only if there are updates
            if (updatedGurantor && Object.keys(updatedGurantor)?.length) {
                updateGurators.push(updatedGurantor);
            }
        }
    }

    if (updateGurators?.length) {
        for (const guarantorKey in updateGurators) {
            const guarantor = updateGurators[guarantorKey] as IGuarantor;

            for (const [key, value] of Object.entries(guarantor)) {
                if (key === 'documents') {
                    guarantor.documents.forEach((doc, index) => {
                        const name = doc.name;
                        const files = doc.files;
                        for (const file of files) {
                            if (file) formData.append(`guarantors[${guarantorKey}][${name}]`, file.file as File);
                        }
                    });
                } else {
                    formData.append(`guarantors[${guarantorKey}][${key}]`, value);
                }
            }
        }
    }

    if (leadDocuments && Object.keys(leadDocuments)) {
        Object.keys(leadDocuments).forEach(dockey => {
            const files = leadDocuments[dockey as keyof IDocument];
            if (files?.length) {
                for (const file of files) {
                    if (file?.file) {
                        formData.append(`lead_documents[${dockey}]`, file.file as File);
                    }
                }
            }
        });
    }

    return formData;
};
export const getUpdatedAllCombinedGuratorData = (exsistingGurators: IGuarantor[] | null, leadGurantor: IGuarantor[]) => {
    const formData = new FormData();

    // process guarantors

    const updateGurators: Record<string, any>[] = [];
    if (leadGurantor?.length) {
        for (const guarantor of leadGurantor) {
            const existingGurator = exsistingGurators?.find(gu => gu.id === guarantor.id);

            if (!existingGurator) {
                updateGurators.push(guarantor);
                continue;
            }

            const updatedGurantor: Record<string, any> = { id: guarantor.id };

            // List of fields to check for updates
            const fieldsToCheck: (keyof typeof guarantor)[] = ['first_name', 'last_name', 'cnic_number', 'email', 'primary_mobile', 'secondary_mobile', 'province', 'city'];

            fieldsToCheck.forEach(field => {
                if ((existingGurator as any)[field]?.toString() !== guarantor[field]?.toString()) {
                    updatedGurantor[field] = guarantor[field];
                }
            });
            if (guarantor?.documents?.length) {
                guarantor.documents.forEach(doc => {
                    const name = doc.name;
                    const files = doc.files;
                    const filterdFiles = [];
                    for (const file of files) {
                        if (file?.file) filterdFiles.push(file);
                    }

                    if (filterdFiles?.length) {
                        if (updatedGurantor.documents) updatedGurantor.documents.push({ name, files: filterdFiles });
                        else updatedGurantor.documents = [{ name, files: filterdFiles }];
                    }
                });
            }

            // Push only if there are updates
            if (updatedGurantor && Object.keys(updatedGurantor)?.length) {
                updateGurators.push(updatedGurantor);
            }
        }
    }

    if (updateGurators?.length) {
        for (const guarantorKey in updateGurators) {
            const guarantor = updateGurators[guarantorKey] as IGuarantor;

            for (const [key, value] of Object.entries(guarantor)) {
                if (key === 'documents') {
                    guarantor.documents.forEach((doc, index) => {
                        const name = doc.name;
                        const files = doc.files;
                        for (const file of files) {
                            if (file) formData.append(`guarantors[${guarantorKey}][${name}]`, file.file as File);
                        }
                    });
                } else {
                    formData.append(`guarantors[${guarantorKey}][${key}]`, value);
                }
            }
        }
    }

    return formData;
};

export const checkLeadInforDataForErrors = (leadInfo: ILeadValues): string => {
    if (leadInfo.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(leadInfo.email)) return 'Enter a valid email address';
    }

    if (leadInfo.cnic_number) {
        const cnicRegex = /^\d{5}-\d{7}-\d$/;
        if (!cnicRegex.test(leadInfo.cnic_number)) return 'CNIC format must be XXXXX-XXXXXXX-X';
    }

    return '';
};
export const checkLeadProductsDataForErrors = (leadProducts: IProduct[]): string => {
    let error = '';
    const regex = /^[0-9]+$/;
    const AprRegex = /^\d+(\.\d+)?$/;

    leadProducts.forEach(product => {
        if (product.down_payment) {
            if (!regex.test(product.down_payment)) error = 'Down Payment is not a number';
        }
        if (product.token_amount) {
            if (!regex.test(product.token_amount)) error = 'Token amount is not a number';
        }
        if (product.apr) {
            if (!AprRegex.test(product.apr)) error = 'Apr is not a number';
        }
        if (product.retail_price) {
            if (!regex.test(product.retail_price)) error = 'Retail Price is not a number';
        }
    });

    return error;
};

export const checkLeadGurantorsDataForErrors = (leadGurantor: IGuarantor[]): string => {
    let error = '';
    const regex = /^[0-9]+$/;
    const guratorsList: IGuarantor[] = [];
    leadGurantor.forEach(gurantor => {
        if (gurantor.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(gurantor.email)) error = 'Enter a valid email address';
        }

        if (gurantor.cnic_number) {
            const cnicRegex = /^\d{5}-\d{7}-\d$/;
            if (!cnicRegex.test(gurantor.cnic_number)) error = 'CNIC format must be XXXXX-XXXXXXX-X';
        }
        if (guratorsList.find(gu => gu.email === gurantor.email || gu.email === gurantor.primary_mobile || gu.cnic_number === gurantor.cnic_number)) {
            error = 'Duplicate Guarantors.Each Guarantor should have unique email, cnic and phone number';
        } else {
            guratorsList.push(gurantor);
        }
    });

    return error;
};
export function buildInQuery(key: string, values: string[]) {
    return values.length ? `${key},in,${values.join('|')};` : '';
}
